import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { getActiveFolder, listFolders } from "@/api/lookups";
import type { Folder, UUID } from "@/api/types";
import { useActiveFolderStore } from "@/stores/activeFolder";
import { useAuthStore } from "@/stores/auth";

/**
 * Combined folders + active-folder hook (papki-tenants v1).
 *
 * Fetches the user's folder list and the server-stored active folder, then
 * mirrors the active folder id into the Zustand slice so non-React consumers
 * (e.g. ad-hoc query-key builders) and React consumers share one source.
 *
 * Disabled when no user is logged in — avoids a noisy 401 in the network tab
 * on the login page.
 */
export function useFolders(): {
  folders: Folder[];
  activeFolderId: UUID | null;
  isLoading: boolean;
} {
  const userId = useAuthStore((s) => s.userId);
  const enabled = userId !== null;
  const setActive = useActiveFolderStore((s) => s.setActive);
  const activeFolderId = useActiveFolderStore((s) => s.activeFolderId);

  const foldersQ = useQuery({
    queryKey: ["folders", "list"],
    queryFn: () => listFolders(),
    enabled,
  });

  const activeQ = useQuery({
    queryKey: ["folders", "active"],
    queryFn: () => getActiveFolder(),
    enabled,
  });

  // Mirror the server-known active folder into the Zustand slice so other
  // consumers (e.g. MeetingsRegistry's `X-Active-Folder` header) read one
  // value. Only fires when the server result actually differs to avoid
  // clobbering an in-flight optimistic update from the dropdown.
  useEffect(() => {
    const serverId = activeQ.data?.folder_id ?? null;
    if (serverId !== activeFolderId) setActive(serverId);
    // We deliberately depend on the *fetched* value only; including the
    // store id here would create a feedback loop with the dropdown's
    // optimistic write.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQ.data?.folder_id]);

  return {
    folders: foldersQ.data ?? [],
    activeFolderId,
    isLoading: foldersQ.isLoading || activeQ.isLoading,
  };
}
